import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";
import { useHomepageConfig } from "@/hooks/useHomepageConfig";
import { ToastProvider, useAdminToast } from "@/components/admin/AdminToast";
import AdminPreview from "@/components/admin/AdminPreview";
import AdminSearchBar from "@/components/admin/AdminSearchBar";
import SortableTable from "@/components/admin/SortableTable";
import HomepageEditor from "@/components/admin/HomepageEditor";

// Helper: convierte link de Google Drive a URL directo de imagen
function toDriveDirectUrl(url: string): string {
  if (!url) return url;
  const trimmed = url.trim();
  // Formato directo ya
  if (trimmed.includes("googleusercontent.com")) {
    return trimmed;
  }
  if (trimmed.includes("drive.google.com/thumbnail?") || trimmed.includes("drive.google.com/uc?")) {
    return trimmed;
  }
  // Extraer ID del formato /file/d/ID/view
  const fileMatch = trimmed.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
  if (fileMatch) {
    return `https://drive.google.com/thumbnail?id=${fileMatch[1]}\u0026sz=w800`;
  }
  // Formato open?id=ID
  const openMatch = trimmed.match(/[?\u0026]id=([a-zA-Z0-9_-]+)/);
  if (openMatch) {
    return `https://drive.google.com/thumbnail?id=${openMatch[1]}\u0026sz=w800`;
  }
  return trimmed;
}

type Tab =
  | "inicio"
  | "datos"
  | "noticias"
  | "citas"
  | "documentos"
  | "equipo"
  | "heroes"
  | "timeline"
  | "footer"
  | "footer_links"
  | "redes_sociales"
  | "comunas"
  | "indicadores"
  | "vision_mision"
  | "objetivos"
  | "especialistas"
  | "evidencias_filtros"
  | "etapas_estudio";

const IMAGE_FIELDS: Record<string, string[]> = {
  noticias: ["imagen"],
  citas: ["avatar"],
  equipo: ["foto"],
  heroes: ["background_image"],
  comunas: ["imagen"],
  especialistas: ["foto"],
};

const DRIVE_FILE_FIELDS: Record<string, string[]> = {
  documentos: ["archivo_url"],
};

const TABS_WITH_ORDER: Tab[] = [
  "timeline",
  "comunas",
  "indicadores",
  "vision_mision",
  "objetivos",
  "especialistas",
  "redes_sociales",
  "footer_links",
  "evidencias_filtros",
  "etapas_estudio",
];

const TAB_ICONS: Record<Tab, string> = {
  inicio: "ri-home-line",
  datos: "ri-bar-chart-line",
  noticias: "ri-newspaper-line",
  citas: "ri-double-quotes-l",
  documentos: "ri-file-list-line",
  equipo: "ri-team-line",
  heroes: "ri-image-line",
  timeline: "ri-time-line",
  footer: "ri-layout-bottom-line",
  footer_links: "ri-links-line",
  redes_sociales: "ri-share-line",
  comunas: "ri-map-pin-line",
  indicadores: "ri-pie-chart-line",
  vision_mision: "ri-eye-line",
  objetivos: "ri-flag-line",
  especialistas: "ri-user-star-line",
  evidencias_filtros: "ri-filter-3-line",
  etapas_estudio: "ri-stack-line",
};

interface FieldGroup {
  label: string;
  fields: string[];
}

const FIELD_GROUPS: Record<Tab, FieldGroup[]> = {
  noticias: [
    { label: "Básico", fields: ["titulo", "subtitulo", "fecha", "comuna", "categoria", "segmento"] },
    { label: "Contenido", fields: ["extracto"] },
    { label: "Media", fields: ["imagen"] },
  ],
  citas: [
    { label: "Básico", fields: ["texto", "autor", "cargo", "sector", "fecha"] },
    { label: "Media", fields: ["avatar", "likes"] },
  ],
  documentos: [
    { label: "Básico", fields: ["titulo", "categoria", "ano", "mes", "tipo", "tamano"] },
    { label: "Contenido", fields: ["descripcion"] },
    { label: "Archivo", fields: ["archivo_url"] },
  ],
  equipo: [
    { label: "Básico", fields: ["nombre", "cargo", "direccion", "email", "telefono"] },
    { label: "Media", fields: ["foto"] },
  ],
  heroes: [
    { label: "Básico", fields: ["page", "nav_label", "seo_title", "tagline", "title", "subtitle", "description", "cta_text", "cta_link"] },
    { label: "Media", fields: ["background_image", "overlay_gradient"] },
  ],
  timeline: [
    { label: "Básico", fields: ["orden", "titulo", "descripcion", "fecha"] },
    { label: "Estado", fields: ["estado_tipo", "estado_color"] },
  ],
  footer: [
    { label: "Contenido", fields: ["descripcion", "direccion", "email", "web", "rut", "telefono", "copyright"] },
  ],
  footer_links: [
    { label: "Básico", fields: ["orden", "label", "url", "seccion", "activo"] },
  ],
  redes_sociales: [
    { label: "Básico", fields: ["orden", "nombre", "url", "icono", "color_class"] },
  ],
  comunas: [
    { label: "Básico", fields: ["id", "nombre", "habitantes", "especialistas", "centros_medicos", "orden"] },
    { label: "Mapa", fields: ["dot_cx", "dot_cy", "path_indices"] },
    { label: "Media", fields: ["imagen"] },
  ],
  indicadores: [
    { label: "Básico", fields: ["orden", "valor", "titulo", "subtitulo", "posicion"] },
  ],
  vision_mision: [
    { label: "Básico", fields: ["orden", "tipo", "titulo", "descripcion", "icono"] },
  ],
  objetivos: [
    { label: "Básico", fields: ["orden", "titulo", "descripcion"] },
  ],
  especialistas: [
    { label: "Básico", fields: ["orden", "nombre", "cargo", "institucion", "email", "telefono"] },
    { label: "Media", fields: ["foto"] },
  ],
  evidencias_filtros: [
    { label: "Básico", fields: ["orden", "nombre", "tipo", "activo"] },
  ],
  etapas_estudio: [
    { label: "Básico", fields: ["orden", "titulo", "descripcion"] },
  ],
};

function getFields(tab: Tab): string[] {
  return FIELD_GROUPS[tab].flatMap((g) => g.fields);
}

interface NoticiasFiltroTipo {
  id: number;
  label: string;
  activo: boolean;
  orden: number;
}

interface NoticiasFiltroOpcion {
  id: number;
  filtro_tipo_id: number;
  nombre: string;
  activo: boolean;
  orden: number;
}

function AdminPanelInner() {
  const [activeTab, setActiveTab] = useState<Tab>("inicio");
  const [dataLoading, setDataLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dbData, setDbData] = useState<Record<Tab, any[]>>({
    inicio: [],
    datos: [],
    noticias: [],
    citas: [],
    documentos: [],
    equipo: [],
    heroes: [],
    timeline: [],
    footer: [],
    footer_links: [],
    redes_sociales: [],
    comunas: [],
    indicadores: [],
    vision_mision: [],
    objetivos: [],
    especialistas: [],
    evidencias_filtros: [],
    etapas_estudio: [],
  });
  const [editItem, setEditItem] = useState<any | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<Record<string, any>>();
  const [fileUploading, setFileUploading] = useState<Record<string, boolean>>();
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: number; table: string } | null>(null);
  const [orderChanged, setOrderChanged] = useState(false);
  const [pendingReorder, setPendingReorder] = useState<any[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [datosSaving, setDatosSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>();
  const { user, loading: authLoading, signOut } = useAuth();
  const { showToast } = useAdminToast();
  const navigate = useNavigate();
  const { data: homepageConfig, loading: homepageLoading, refetch: refetchHomepage } = useHomepageConfig();
  const [datosTerritorioVisible, setDatosTerritorioVisible] = useState<boolean>(
    homepageConfig?.datos_territorio_visible ?? true
  );
  const [inicioTerritorioVisible, setInicioTerritorioVisible] = useState<boolean>(
    homepageConfig?.inicio_territorio_visible ?? true
  );

  // Noticias filters state
  const [noticiasFiltrosTipos, setNoticiasFiltrosTipos] = useState<NoticiasFiltroTipo[]>([]);
  const [noticiasFiltrosOpciones, setNoticiasFiltrosOpciones] = useState<NoticiasFiltroOpcion[]>([]);
  const [showNoticiasFiltros, setShowNoticiasFiltros] = useState(false);
  const [noticiasFiltroForm, setNoticiasFiltroForm] = useState<{ tipo: 'tipo' | 'opcion'; editId?: number; data: Record<string, any> } | null>(null);
  const [noticiasFiltrosLoading, setNoticiasFiltrosLoading] = useState(false);

  // Auth redirect
  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/admin/login");
    }
  }, [authLoading, user, navigate]);

  // Fetch data
  useEffect(() => {
    if (activeTab === "inicio" || activeTab === "datos") return;
    fetchAllData();
  }, [activeTab]);

  // Sync datosTerritorioVisible from config
  useEffect(() => {
    if (homepageConfig) {
      setDatosTerritorioVisible(homepageConfig.datos_territorio_visible);
      setInicioTerritorioVisible(homepageConfig.inicio_territorio_visible);
    }
  }, [homepageConfig]);

  // Fetch noticias filtros when on noticias tab
  useEffect(() => {
    if (activeTab === "noticias") {
      fetchNoticiasFiltros();
    }
  }, [activeTab]);

  const fetchNoticiasFiltros = async () => {
    setNoticiasFiltrosLoading(true);
    try {
      const [tiposRes, opcionesRes] = await Promise.all([
        supabase.from("noticias_filtros_tipos").select("*").order("orden"),
        supabase.from("noticias_filtros_opciones").select("*").order("orden"),
      ]);
      setNoticiasFiltrosTipos(tiposRes.data || []);
      setNoticiasFiltrosOpciones(opcionesRes.data || []);
    } catch {
      showToast("Error cargando filtros de noticias", "error");
    } finally {
      setNoticiasFiltrosLoading(false);
    }
  };

  const fetchAllData = async () => {
    setDataLoading(true);
    setError(null);
    try {
      const tables: Tab[] = [
        "noticias",
        "citas",
        "documentos",
        "equipo",
        "heroes",
        "timeline",
        "footer",
        "footer_links",
        "redes_sociales",
        "comunas",
        "indicadores",
        "vision_mision",
        "objetivos",
        "especialistas",
        "evidencias_filtros",
        "etapas_estudio",
      ];
      const orderBys: Record<string, { column: string; ascending: boolean }> = {
        noticias: { column: "created_at", ascending: false },
        citas: { column: "created_at", ascending: false },
        documentos: { column: "created_at", ascending: false },
        equipo: { column: "created_at", ascending: false },
        heroes: { column: "id", ascending: true },
        timeline: { column: "orden", ascending: true },
        footer: { column: "id", ascending: true },
        footer_links: { column: "orden", ascending: true },
        redes_sociales: { column: "orden", ascending: true },
        comunas: { column: "orden", ascending: true },
        indicadores: { column: "orden", ascending: true },
        vision_mision: { column: "orden", ascending: true },
        objetivos: { column: "orden", ascending: true },
        especialistas: { column: "orden", ascending: true },
        evidencias_filtros: { column: "orden", ascending: true },
        etapas_estudio: { column: "orden", ascending: true },
      };

      const results = await Promise.all(
        tables.map((t) =>
          supabase
            .from(t)
            .select("*")
            .order(orderBys[t].column, { ascending: orderBys[t].ascending })
        )
      );

      const newData: Record<Tab, any[]> = { ...dbData };
      tables.forEach((t, i) => {
        newData[t] = results[i].data || [];
      });
      setDbData(newData);
    } catch {
      setError("Error cargando datos desde Supabase.");
      showToast("Error cargando datos", "error");
    } finally {
      setDataLoading(false);
    }
  };

  // Track changes when formData changes
  useEffect(() => {
    if (showForm) {
      setHasChanges(true);
    }
  }, [formData]);

  const currentData = dbData[activeTab] ?? [];
  const filteredData = useMemo(() => {
    if (!searchQuery || activeTab === "inicio" || activeTab === "datos") return currentData;
    const q = searchQuery.toLowerCase();
    return currentData.filter((item) =>
      Object.values(item).some((val) =>
        String(val).toLowerCase().includes(q)
      )
    );
  }, [currentData, searchQuery]);

  const tabs = useMemo(
    () =>
      (Object.keys(TAB_ICONS) as Tab[]).map((id) => ({
        id,
        label: id === "inicio"
          ? "Inicio (página)"
          : id === "datos"
          ? "La Región (página)"
          : id
              .replace("_", " ")
              .replace(/\b\w/g, (l) => l.toUpperCase()),
        icon: TAB_ICONS[id],
        count: id === "inicio" || id === "datos" ? 1 : dbData[id]?.length ?? 0,
      })),
    [dbData]
  );

  const handleDelete = async (id: number, table: string) => {
    setDeleteConfirm({ id, table });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    try {
      const { error: err } = await supabase
        .from(deleteConfirm.table)
        .delete()
        .eq("id", deleteConfirm.id);
      if (err) throw err;
      showToast("Registro eliminado", "success");
      fetchAllData();
    } catch {
      showToast("Error al eliminar", "error");
    } finally {
      setDeleteConfirm(null);
    }
  };

  const handleEdit = (item: any) => {
    setEditItem(item);
    setFormData({ ...item });
    setHasChanges(false);
    setUploadError(null);
    setFileUploading({});
    setShowForm(true);
    setSearchQuery("");
  };

  const handleNew = () => {
    setEditItem(null);
    const defaults: Record<string, any> = {};
    if (activeTab === "footer_links") {
      defaults.seccion = "acceso_directo";
      defaults.activo = true;
    }
    setFormData(defaults);
    setHasChanges(false);
    setUploadError(null);
    setFileUploading({});
    setShowForm(true);
    setSearchQuery("");
  };

  const handleFileUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    fieldName: string,
    type: "image" | "pdf"
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (type === "pdf" && file.type !== "application/pdf") {
      setUploadError("Solo se permiten archivos PDF.");
      return;
    }
    if (type === "image" && !file.type.startsWith("image/")) {
      setUploadError("Solo se permiten archivos de imagen.");
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      setUploadError("El archivo no puede superar los 20MB.");
      return;
    }
    setFileUploading((prev) => ({ ...prev, [fieldName]: true }));
    setUploadError(null);
    try {
      const bucket = type === "pdf" ? "documentos" : "imagenes";
      const fileName = `${Date.now()}_${file.name.replace(/\s+/g, "_")}`;
      const { error: uploadError } = await supabase.storage
        .from(bucket)
        .upload(fileName, file, { contentType: file.type });
      if (uploadError) throw uploadError;
      const { data: publicUrlData } = supabase.storage
        .from(bucket)
        .getPublicUrl(fileName);
      setFormData((prev) => ({ ...prev, [fieldName]: publicUrlData.publicUrl }));
      showToast(type === "pdf" ? "PDF subido" : "Imagen subida", "success");
    } catch (err: any) {
      setUploadError(err.message || "Error al subir el archivo.");
      showToast("Error al subir archivo", "error");
    } finally {
      setFileUploading((prev) => ({ ...prev, [fieldName]: false }));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const table = activeTab;
      if (editItem?.id) {
        await supabase.from(table).update(formData).eq("id", editItem.id);
        showToast("Cambios guardados", "success");
      } else {
        await supabase.from(table).insert([formData]);
        showToast("Registro creado", "success");
      }
      setShowForm(false);
      setEditItem(null);
      setFormData({});
      setHasChanges(false);
      fetchAllData();
    } catch {
      showToast("Error al guardar", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleReorder = async (newItems: any[]) => {
    setPendingReorder(newItems);
    setOrderChanged(true);
  };

  const saveOrder = async () => {
    if (!pendingReorder) return;
    setSaving(true);
    try {
      const updates = pendingReorder.map((item, idx) => ({
        id: item.id,
        orden: idx + 1,
      }));
      await Promise.all(
        updates.map((u) =>
          supabase.from(activeTab).update({ orden: u.orden }).eq("id", u.id)
        )
      );
      showToast("Orden guardado", "success");
      setOrderChanged(false);
      setPendingReorder(null);
      fetchAllData();
    } catch {
      showToast("Error al guardar orden", "error");
    } finally {
      setSaving(false);
    }
  };

  const isImageField = (field: string) => IMAGE_FIELDS[activeTab]?.includes(field);
  const isDriveFileField = (field: string) => DRIVE_FILE_FIELDS[activeTab]?.includes(field);
  const hasOrderField = TABS_WITH_ORDER.includes(activeTab);

  const handleChangeField = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Noticias filtros helpers
  const handleSaveNoticiasFiltro = async () => {
    if (!noticiasFiltroForm) return;
    setSaving(true);
    try {
      const table = noticiasFiltroForm.tipo === 'tipo' ? 'noticias_filtros_tipos' : 'noticias_filtros_opciones';
      if (noticiasFiltroForm.editId) {
        await supabase.from(table).update(noticiasFiltroForm.data).eq('id', noticiasFiltroForm.editId);
        showToast("Filtro actualizado", "success");
      } else {
        await supabase.from(table).insert([noticiasFiltroForm.data]);
        showToast("Filtro creado", "success");
      }
      setNoticiasFiltroForm(null);
      fetchNoticiasFiltros();
    } catch {
      showToast("Error al guardar filtro", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteNoticiasFiltro = async (tipo: 'tipo' | 'opcion', id: number) => {
    if (!window.confirm('¿Eliminar este filtro?')) return;
    const table = tipo === 'tipo' ? 'noticias_filtros_tipos' : 'noticias_filtros_opciones';
    try {
      await supabase.from(table).delete().eq('id', id);
      showToast("Filtro eliminado", "success");
      fetchNoticiasFiltros();
    } catch {
      showToast("Error al eliminar filtro", "error");
    }
  };

  const renderFieldInput = (field: string) => {
    if (isImageField(field)) {
      return (
        <div>
          <div className="flex items-center gap-1.5 mb-1.5">
            <i className="ri-link text-xs text-amber-500" />
            <span className="text-[11px] text-gray-500">
              Pegar link público de Google Drive
            </span>
          </div>
          <input
            type="text"
            value={formData[field] || ""}
            onChange={(e) => handleChangeField(field, e.target.value)}
            placeholder="https://drive.google.com/file/d/.../preview"
            className="w-full h-10 px-3 rounded-lg border border-gray-200 text-sm text-gray-900 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
          />
          <p className="mt-1.5 text-[11px] text-amber-600 flex items-start gap-1">
            <i className="ri-information-line mt-px flex-shrink-0" />
            <span>El link de Drive debe ser público para que la imagen se muestre correctamente en el sitio.</span>
          </p>
          {formData?.[field] && (
            <img
              src={toDriveDirectUrl(formData[field])}
              alt="Preview"
              className="mt-3 w-32 h-20 object-cover rounded-lg border border-gray-200"
            />
          )}
        </div>
      );
    }
    if (isDriveFileField(field)) {
      return (
        <div>
          <div className="flex items-center gap-1.5 mb-1.5">
            <i className="ri-link text-xs text-amber-500" />
            <span className="text-[11px] text-gray-500">
              Pegar link público de Google Drive
            </span>
          </div>
          <input
            type="text"
            value={formData[field] || ""}
            onChange={(e) => handleChangeField(field, e.target.value)}
            placeholder="https://drive.google.com/file/d/.../view"
            className="w-full h-10 px-3 rounded-lg border border-gray-200 text-sm text-gray-900 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
          />
          <p className="mt-1.5 text-[11px] text-amber-600 flex items-start gap-1">
            <i className="ri-information-line mt-px flex-shrink-0" />
            <span>El link de Drive debe ser público para que el archivo se pueda descargar correctamente en el sitio.</span>
          </p>
          {formData?.[field] && (
            <a
              href={toDriveDirectUrl(formData[field])}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-gray-200 text-sm text-primary hover:bg-primary/5 transition-colors cursor-pointer"
            >
              <i className="ri-file-pdf-2-line" />
              Ver archivo
            </a>
          )}
        </div>
      );
    }

    // Special fields
    if (field === "extracto" || field === "descripcion") {
      return (
        <textarea
          value={formData[field] || ""}
          onChange={(e) => handleChangeField(field, e.target.value)}
          rows={4}
          className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-900 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary resize-none"
        />
      );
    }
    if (field === "likes" || field === "habitantes" || field === "especialistas" || field === "centros_medicos" || field === "orden" || field === "ano" || field === "stock") {
      return (
        <input
          type="number"
          value={formData[field] ?? ""}
          onChange={(e) => handleChangeField(field, e.target.value === "" ? "" : Number(e.target.value))}
          className="w-full h-10 px-3 rounded-lg border border-gray-200 text-sm text-gray-900 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
        />
      );
    }
    if (field === "dot_cx" || field === "dot_cy" || field === "valor") {
      return (
        <input
          type="number"
          step="0.1"
          value={formData[field] ?? ""}
          onChange={(e) => handleChangeField(field, e.target.value === "" ? "" : parseFloat(e.target.value))}
          className="w-full h-10 px-3 rounded-lg border border-gray-200 text-sm text-gray-900 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
        />
      );
    }
    if (field === "estado_color") {
      return (
        <div className="flex items-center gap-3">
          <input
            type="color"
            value={formData[field] || "#E45B2D"}
            onChange={(e) => handleChangeField(field, e.target.value)}
            className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer"
          />
          <input
            type="text"
            value={formData[field] || ""}
            onChange={(e) => handleChangeField(field, e.target.value)}
            className="flex-1 h-10 px-3 rounded-lg border border-gray-200 text-sm text-gray-900 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
          />
        </div>
      );
    }

    if (field === "seccion" && activeTab === "footer_links") {
      return (
        <select
          value={formData[field] || "acceso_directo"}
          onChange={(e) => handleChangeField(field, e.target.value)}
          className="w-full h-10 px-3 rounded-lg border border-gray-200 text-sm text-gray-900 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
        >
          <option value="acceso_directo">Sitios de interés</option>
          <option value="sitios_interes">Mapa del sitio</option>
        </select>
      );
    }
    if (field === "activo" && activeTab === "footer_links") {
      return (
        <div className="flex items-center gap-3">
          <input
            id="activo-field"
            type="checkbox"
            checked={formData[field] ?? true}
            onChange={(e) => handleChangeField(field, e.target.checked)}
            className="w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer"
          />
          <label htmlFor="activo-field" className="text-sm text-gray-700 cursor-pointer">
            {formData[field] ?? true ? "Activo" : "Inactivo"}
          </label>
        </div>
      );
    }

    return (
      <input
        type="text"
        value={formData[field] || ""}
        onChange={(e) => handleChangeField(field, e.target.value)}
        className="w-full h-10 px-3 rounded-lg border border-gray-200 text-sm text-gray-900 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
      />
    );
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-gray-200 bg-white sticky top-0 z-50">
        <div className="w-full px-4 md:px-8 lg:px-12 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
              <i className="ri-settings-3-line text-white text-lg"></i>
            </div>
            <h1 className="text-lg font-bold text-gray-900">
              Panel Admin
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/admin/manual"
              className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
            >
              <i className="ri-book-open-line mr-1.5" />
              Manual
            </a>
            <a
              href="/"
              className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
            >
              <i className="ri-external-link-line mr-1.5" />
              Ver sitio
            </a>
            {user && (
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-500 hidden sm:block">
                  {user.email}
                </span>
                <button
                  onClick={signOut}
                  className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <i className="ri-logout-box-r-line mr-1.5" />
                  Salir
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="w-full px-4 md:px-8 lg:px-12 py-6 md:py-8">
        {/* Auth loading */}
        {authLoading && (
          <div className="mb-4 flex items-center gap-3 text-gray-500">
            <i className="ri-loader-4-line animate-spin text-xl" />
            <span className="text-sm">Verificando sesión...</span>
          </div>
        )}

        {/* Error banner */}
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-2.5">
            <i className="ri-error-warning-line text-red-500 text-lg mt-0.5"></i>
            <div className="flex-1">
              <h4 className="text-sm font-medium text-red-700">Error de conexión</h4>
              <p className="text-sm text-red-600 mt-1">{error}</p>
            </div>
            <button
              onClick={fetchAllData}
              className="px-3 py-1.5 rounded-lg border border-red-200 text-xs text-red-600 hover:bg-red-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line mr-1" />
              Reintentar
            </button>
          </div>
        )}

        {/* Loading */}
        {dataLoading && (
          <div className="mb-4 flex items-center gap-3 text-gray-500">
            <i className="ri-loader-4-line animate-spin text-xl" />
            <span className="text-sm">Cargando datos...</span>
          </div>
        )}

        {/* Tabs */}
        <div className="flex items-center gap-1 mb-6 overflow-x-auto pb-1">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => {
                setActiveTab(t.id);
                setShowForm(false);
                setSearchQuery("");
                setOrderChanged(false);
                setShowNoticiasFiltros(false);
              }}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all cursor-pointer h-11 ${
                activeTab === t.id
                  ? "bg-primary text-white shadow-sm"
                  : "bg-white text-gray-600 border border-gray-200 hover:border-primary hover:text-primary"
              }`}
            >
              <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className={t.icon} />
              </span>
              {t.label}
              <span
                className={`text-xs px-2 py-0.5 rounded-full ${
                  activeTab === t.id
                    ? "bg-white/20 text-white"
                    : "bg-gray-100 text-gray-500"
                }`}
              >
                {t.count}
              </span>
            </button>
          ))}
        </div>

        {/* Actions bar */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-2">
            <i className={`${TAB_ICONS[activeTab]} text-primary text-xl`} />
            <h2 className="text-xl font-bold text-gray-900">
              {tabs.find((t) => t.id === activeTab)?.label}
            </h2>
          </div>
          {activeTab !== "inicio" && activeTab !== "datos" && (
            <div className="flex items-center gap-3">
              <AdminSearchBar
                value={searchQuery}
                onChange={setSearchQuery}
                count={filteredData.length}
                total={currentData.length}
              />
              <button
                onClick={fetchAllData}
                disabled={dataLoading}
                className="w-10 h-10 rounded-lg border border-gray-200 flex items-center justify-center text-gray-600 hover:border-primary hover:text-primary transition-colors cursor-pointer disabled:opacity-50"
                title="Recargar datos"
              >
                <i className={`ri-refresh-line ${dataLoading ? "animate-spin" : ""}`} />
              </button>
              <button
                onClick={handleNew}
                className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-primary text-white text-sm rounded-lg hover:bg-primary-dark transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line"></i>
                Nuevo
              </button>
              {activeTab === "documentos" && !showForm && currentData.length > 0 && (
                <button
                  onClick={async () => {
                    if (!window.confirm(`¿Eliminar todos los ${currentData.length} documentos? Esta acción no se puede deshacer.`)) return;
                    setDataLoading(true);
                    try {
                      const { error: err } = await supabase
                        .from("documentos")
                        .delete()
                        .neq("id", 0);
                      if (err) throw err;
                      showToast(`${currentData.length} documentos eliminados`, "success");
                      fetchAllData();
                    } catch {
                      showToast("Error al eliminar documentos", "error");
                    } finally {
                      setDataLoading(false);
                    }
                  }}
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 border border-red-200 text-red-600 text-sm rounded-lg hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-delete-bin-line" />
                  Eliminar todos
                </button>
              )}
            </div>
          )}
        </div>

        {/* Inicio tab: custom homepage editor */}
        {activeTab === "inicio" && (
          <HomepageEditor
            config={homepageConfig}
            loading={homepageLoading}
            onSaved={refetchHomepage}
            showToast={showToast}
          />
        )}

        {/* Datos tab: visibility toggles */}
        {activeTab === "datos" && (
          <div className="max-w-xl border border-gray-200 rounded-xl p-5 md:p-6 bg-white shadow-sm space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <i className="ri-map-line text-primary text-xl" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Sección Territorio</h3>
                <p className="text-sm text-gray-500">Configurar visibilidad de la sección del mapa.</p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 rounded-lg border border-gray-200 bg-gray-50/50">
              <div className="flex items-center h-5">
                <input
                  id="datos-territorio-visible"
                  type="checkbox"
                  checked={datosTerritorioVisible}
                  onChange={(e) => setDatosTerritorioVisible(e.target.checked)}
                  className="w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer"
                />
              </div>
              <div className="flex-1">
                <label htmlFor="datos-territorio-visible" className="block text-sm font-medium text-gray-900 cursor-pointer">
                  Mostrar sección Territorio en la página de La Región
                </label>
                <p className="text-xs text-gray-500 mt-1">
                  {datosTerritorioVisible
                    ? "La sección del mapa se muestra en la página de La Región."
                    : "La sección del mapa está oculta en la página de La Región."}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 rounded-lg border border-gray-200 bg-gray-50/50">
              <div className="flex items-center h-5">
                <input
                  id="inicio-territorio-visible"
                  type="checkbox"
                  checked={inicioTerritorioVisible}
                  onChange={(e) => setInicioTerritorioVisible(e.target.checked)}
                  className="w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer"
                />
              </div>
              <div className="flex-1">
                <label htmlFor="inicio-territorio-visible" className="block text-sm font-medium text-gray-900 cursor-pointer">
                  Mostrar sección Territorio en la página de Inicio
                </label>
                <p className="text-xs text-gray-500 mt-1">
                  {inicioTerritorioVisible
                    ? "La sección del mapa se muestra en la página de Inicio."
                    : "La sección del mapa está oculta en la página de Inicio."}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 pt-4 border-t border-gray-100">
              <button
                onClick={async () => {
                  setDatosSaving(true);
                  try {
                    const { error: err } = await supabase
                      .from("homepage_config")
                      .update({
                        datos_territorio_visible: datosTerritorioVisible,
                        inicio_territorio_visible: inicioTerritorioVisible,
                      })
                      .eq("id", 1);
                    if (err) throw err;
                    showToast("Configuración guardada", "success");
                    await refetchHomepage();
                  } catch {
                    showToast("Error al guardar", "error");
                  } finally {
                    setDatosSaving(false);
                  }
                }}
                disabled={datosSaving}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-white text-sm rounded-lg hover:bg-primary-dark transition-colors cursor-pointer disabled:opacity-50"
              >
                {datosSaving ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <i className="ri-save-line" />
                    Guardar
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Noticias tab with filters section */}
        {activeTab === "noticias" && !showForm && (
          <div className="mb-6 border border-gray-200 rounded-xl bg-white overflow-hidden">
            <button
              onClick={() => setShowNoticiasFiltros(!showNoticiasFiltros)}
              className="w-full flex items-center justify-between px-5 py-3 bg-gray-50/50 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-2">
                <i className="ri-filter-2-line text-primary text-lg" />
                <span className="text-sm font-medium text-gray-900">Filtros de Noticias</span>
                <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">
                  {noticiasFiltrosTipos.length} tipos
                </span>
              </div>
              <i className={`ri-arrow-down-s-line text-gray-400 transition-transform ${showNoticiasFiltros ? "rotate-180" : ""}`} />
            </button>

            {showNoticiasFiltros && (
              <div className="p-5 space-y-6">
                {noticiasFiltrosLoading ? (
                  <div className="flex items-center gap-2 text-gray-500 py-4">
                    <i className="ri-loader-4-line animate-spin" />
                    <span className="text-sm">Cargando filtros...</span>
                  </div>
                ) : (
                  <>
                    {/* Filter types */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wider">Tipos de filtro</h4>
                        <button
                          onClick={() => setNoticiasFiltroForm({ tipo: 'tipo', data: { orden: noticiasFiltrosTipos.length + 1, activo: true } })}
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-primary text-white text-xs rounded-lg hover:bg-primary-dark transition-colors cursor-pointer"
                        >
                          <i className="ri-add-line" />
                          Nuevo tipo
                        </button>
                      </div>
                      <div className="space-y-2">
                        {noticiasFiltrosTipos.map((tipo) => (
                          <div key={tipo.id} className="flex items-center justify-between p-3 rounded-lg border border-gray-200 bg-white">
                            <div className="flex items-center gap-3">
                              <span className="text-xs text-gray-400 font-mono">#{tipo.orden}</span>
                              <span className="text-sm font-medium text-gray-900">{tipo.label}</span>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${tipo.activo ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-500'}`}>
                                {tipo.activo ? 'Activo' : 'Inactivo'}
                              </span>
                            </div>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => setNoticiasFiltroForm({ tipo: 'tipo', editId: tipo.id, data: { ...tipo } })}
                                className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-600 hover:border-primary hover:text-primary transition-colors cursor-pointer"
                                title="Editar"
                              >
                                <i className="ri-pencil-line text-sm" />
                              </button>
                              <button
                                onClick={() => handleDeleteNoticiasFiltro('tipo', tipo.id)}
                                className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-600 hover:border-red-400 hover:text-red-500 transition-colors cursor-pointer"
                                title="Eliminar"
                              >
                                <i className="ri-delete-bin-line text-sm" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Filter options */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wider">Opciones de filtros</h4>
                        <button
                          onClick={() => setNoticiasFiltroForm({ tipo: 'opcion', data: { orden: noticiasFiltrosOpciones.length + 1, activo: true } })}
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-primary text-white text-xs rounded-lg hover:bg-primary-dark transition-colors cursor-pointer"
                        >
                          <i className="ri-add-line" />
                          Nueva opción
                        </button>
                      </div>
                      <div className="space-y-2">
                        {noticiasFiltrosTipos.map((tipo) => (
                          <div key={tipo.id} className="border border-gray-200 rounded-lg overflow-hidden">
                            <div className="bg-gray-50 px-3 py-2 text-xs font-medium text-gray-700">
                              {tipo.label}
                            </div>
                            <div className="divide-y divide-gray-100">
                              {noticiasFiltrosOpciones
                                .filter((op) => op.filtro_tipo_id === tipo.id)
                                .map((op) => (
                                  <div key={op.id} className="flex items-center justify-between px-3 py-2">
                                    <div className="flex items-center gap-3">
                                      <span className="text-xs text-gray-400 font-mono">#{op.orden}</span>
                                      <span className="text-sm text-gray-700">{op.nombre}</span>
                                      <span className={`text-xs px-2 py-0.5 rounded-full ${op.activo ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-500'}`}>
                                        {op.activo ? 'Activo' : 'Inactivo'}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <button
                                        onClick={() => setNoticiasFiltroForm({ tipo: 'opcion', editId: op.id, data: { ...op } })}
                                        className="w-7 h-7 rounded border border-gray-200 flex items-center justify-center text-gray-600 hover:border-primary hover:text-primary transition-colors cursor-pointer"
                                        title="Editar"
                                      >
                                        <i className="ri-pencil-line text-xs" />
                                      </button>
                                      <button
                                        onClick={() => handleDeleteNoticiasFiltro('opcion', op.id)}
                                        className="w-7 h-7 rounded border border-gray-200 flex items-center justify-center text-gray-600 hover:border-red-400 hover:text-red-500 transition-colors cursor-pointer"
                                        title="Eliminar"
                                      >
                                        <i className="ri-delete-bin-line text-xs" />
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              {noticiasFiltrosOpciones.filter((op) => op.filtro_tipo_id === tipo.id).length === 0 && (
                                <div className="px-3 py-2 text-xs text-gray-400 italic">
                                  Sin opciones
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* Form + Preview */}
        {activeTab !== "inicio" && activeTab !== "datos" && showForm && (
          <div className="grid grid-cols-1 gap-6 mb-6">
            {/* Form */}
            <div className="border border-gray-200 rounded-xl p-5 md:p-6 bg-white shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <h3 className="text-lg font-bold text-gray-900">
                    {editItem ? "Editar" : "Nuevo"}{" "}
                    {tabs.find((t) => t.id === activeTab)?.label}
                  </h3>
                  {hasChanges && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 font-medium">
                      <i className="ri-edit-circle-line mr-1" />
                      Sin guardar
                    </span>
                  )}
                </div>
                {editItem && (
                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                    ID #{editItem.id}
                  </span>
                )}
              </div>

              <div className="space-y-6">
                {FIELD_GROUPS[activeTab].map((group) => (
                  <div key={group.label}>
                    <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">
                      {group.label}
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {group.fields.map((field) => (
                        <div
                          key={field}
                          className={
                            isDriveFileField(field) || isImageField(field) || field === "extracto" || field === "descripcion"
                              ? "md:col-span-2"
                              : ""
                          }
                        >
                          <label className="block text-sm font-medium text-gray-700 mb-1.5 capitalize">
                            {field.replace("_", " ")}
                            {field === "archivo_url" && (
                              <span className="text-xs text-gray-400 font-normal ml-1">(PDF)</span>
                            )}
                          </label>
                          {renderFieldInput(field)}
                          {/* Image size hint for equipo foto */}
                          {isImageField(field) && activeTab === "equipo" && (
                            <p className="mt-1.5 text-[11px] text-gray-400">
                              <i className="ri-information-line mr-1" />
                              Proporción 1:1 (cuadrada). Mínimo 400×400px, recomendado 800×800px. Rostro centrado y arriba, fondo neutro. JPG o PNG.
                            </p>
                          )}
                          {/* Image size hint for especialistas foto */}
                          {isImageField(field) && activeTab === "especialistas" && (
                            <p className="mt-1.5 text-[11px] text-gray-400">
                              <i className="ri-information-line mr-1" />
                              Proporción 1:1 (cuadrada). Mínimo 400×400px, recomendado 800×800px. Rostro centrado y arriba, fondo neutro. JPG o PNG.
                            </p>
                          )}
                          {/* Image size hint for heroes background */}
                          {isImageField(field) && activeTab === "heroes" && (
                            <p className="mt-1.5 text-[11px] text-gray-400">
                              <i className="ri-information-line mr-1" />
                              Proporción 16:9 (panorámica). Mínimo 1920×1080px, recomendado 2560×1440px. JPG o PNG.
                            </p>
                          )}
                          {/* Image size hint for comunas imagen */}
                          {isImageField(field) && activeTab === "comunas" && (
                            <p className="mt-1.5 text-[11px] text-gray-400">
                              <i className="ri-information-line mr-1" />
                              Proporción 16:9 (panorámica). Mínimo 800×450px, recomendado 1200×675px. JPG o PNG.
                            </p>
                          )}
                          {/* Image size hint for noticias imagen */}
                          {isImageField(field) && activeTab === "noticias" && (
                            <p className="mt-1.5 text-[11px] text-gray-400">
                              <i className="ri-information-line mr-1" />
                              Proporción 16:9 (panorámica). Mínimo 800×450px, recomendado 1200×675px. JPG o PNG.
                            </p>
                          )}
                          {/* Image size hint for citas avatar */}
                          {isImageField(field) && activeTab === "citas" && (
                            <p className="mt-1.5 text-[11px] text-gray-400">
                              <i className="ri-information-line mr-1" />
                              Proporción 1:1 (cuadrada). Mínimo 200×200px, recomendado 400×400px. Rostro centrado. JPG o PNG.
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {uploadError && (
                <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                  <i className="ri-error-warning-line text-red-500 text-sm mt-0.5" />
                  <p className="text-xs text-red-600">{uploadError}</p>
                </div>
              )}

              <div className="flex items-center gap-2 mt-6 pt-4 border-t border-gray-100">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-white text-sm rounded-lg hover:bg-primary-dark transition-colors cursor-pointer disabled:opacity-50"
                >
                  {saving ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      Guardando...
                    </>
                  ) : (
                    <>
                      <i className="ri-save-line" />
                      Guardar
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    if (hasChanges && !window.confirm("¿Descartar cambios sin guardar?")) {
                      return;
                    }
                    setShowForm(false);
                    setEditItem(null);
                    setFormData({});
                    setUploadError(null);
                    setHasChanges(false);
                  }}
                  className="px-5 py-2.5 border border-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Table */}
        {activeTab !== "inicio" && activeTab !== "datos" && !showForm && (
          <>
            <SortableTable
              data={filteredData}
              fields={getFields(activeTab)}
              onEdit={handleEdit}
              onDelete={(id) => handleDelete(id, activeTab)}
              onReorder={handleReorder}
              hasOrderField={hasOrderField}
            />

            {/* Save order button */}
            {hasOrderField && orderChanged && (
              <div className="mt-4 flex items-center justify-between bg-primary/5 border border-primary/20 rounded-lg p-4">
                <div className="flex items-center gap-2 text-sm text-primary">
                  <i className="ri-drag-move-line" />
                  <span>Has reordenado los elementos. Guarda el orden para aplicar los cambios.</span>
                </div>
                <button
                  onClick={saveOrder}
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white text-sm rounded-lg hover:bg-primary-dark transition-colors cursor-pointer disabled:opacity-50"
                >
                  {saving ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      Guardando...
                    </>
                  ) : (
                    <>
                      <i className="ri-save-line mr-1.5" />
                      Guardar orden
                    </>
                  )}
                </button>
              </div>
            )}
          </>
        )}

        {/* Info */}
        {activeTab !== "inicio" && activeTab !== "datos" && !showForm && (
          <div className="mt-6 bg-primary/5 border border-primary/10 rounded-lg p-4">
            <div className="flex items-start gap-2.5">
              <i className="ri-information-line text-primary text-lg mt-0.5"></i>
              <div>
                <h4 className="text-sm font-medium text-gray-900">
                  Datos en Supabase
                </h4>
                <p className="text-sm text-gray-600 mt-1">
                  Los datos se sincronizan directamente desde la base de datos.
                  {hasOrderField && (
                    <>
                      {" "}Usa el ícono
                      <i className="ri-drag-move-2-line mx-1" />
                      para reordenar los elementos.
                    </>
                  )}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Noticias Filtro Form Modal */}
      {noticiasFiltroForm && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              {noticiasFiltroForm.editId ? 'Editar' : 'Nuevo'} {noticiasFiltroForm.tipo === 'tipo' ? 'tipo de filtro' : 'opción'}
            </h3>
            <div className="space-y-4">
              {noticiasFiltroForm.tipo === 'tipo' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                    <input
                      type="text"
                      value={noticiasFiltroForm.data.label || ''}
                      onChange={(e) => setNoticiasFiltroForm(prev => prev ? { ...prev, data: { ...prev.data, label: e.target.value } } : null)}
                      className="w-full h-10 px-3 rounded-lg border border-gray-200 text-sm text-gray-900 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Orden</label>
                    <input
                      type="number"
                      value={noticiasFiltroForm.data.orden || ''}
                      onChange={(e) => setNoticiasFiltroForm(prev => prev ? { ...prev, data: { ...prev.data, orden: Number(e.target.value) } } : null)}
                      className="w-full h-10 px-3 rounded-lg border border-gray-200 text-sm text-gray-900 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    <input
                      id="noticias-filtro-activo"
                      type="checkbox"
                      checked={noticiasFiltroForm.data.activo ?? true}
                      onChange={(e) => setNoticiasFiltroForm(prev => prev ? { ...prev, data: { ...prev.data, activo: e.target.checked } } : null)}
                      className="w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer"
                    />
                    <label htmlFor="noticias-filtro-activo" className="text-sm text-gray-700 cursor-pointer">
                      {noticiasFiltroForm.data.activo ?? true ? 'Activo' : 'Inactivo'}
                    </label>
                  </div>
                </>
              )}
              {noticiasFiltroForm.tipo === 'opcion' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de filtro</label>
                    <select
                      value={noticiasFiltroForm.data.filtro_tipo_id || ''}
                      onChange={(e) => setNoticiasFiltroForm(prev => prev ? { ...prev, data: { ...prev.data, filtro_tipo_id: Number(e.target.value) } } : null)}
                      className="w-full h-10 px-3 rounded-lg border border-gray-200 text-sm text-gray-900 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                    >
                      <option value="">Seleccionar...</option>
                      {noticiasFiltrosTipos.map((t) => (
                        <option key={t.id} value={t.id}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                    <input
                      type="text"
                      value={noticiasFiltroForm.data.nombre || ''}
                      onChange={(e) => setNoticiasFiltroForm(prev => prev ? { ...prev, data: { ...prev.data, nombre: e.target.value } } : null)}
                      className="w-full h-10 px-3 rounded-lg border border-gray-200 text-sm text-gray-900 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Orden</label>
                    <input
                      type="number"
                      value={noticiasFiltroForm.data.orden || ''}
                      onChange={(e) => setNoticiasFiltroForm(prev => prev ? { ...prev, data: { ...prev.data, orden: Number(e.target.value) } } : null)}
                      className="w-full h-10 px-3 rounded-lg border border-gray-200 text-sm text-gray-900 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    <input
                      id="noticias-opcion-activo"
                      type="checkbox"
                      checked={noticiasFiltroForm.data.activo ?? true}
                      onChange={(e) => setNoticiasFiltroForm(prev => prev ? { ...prev, data: { ...prev.data, activo: e.target.checked } } : null)}
                      className="w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer"
                    />
                    <label htmlFor="noticias-opcion-activo" className="text-sm text-gray-700 cursor-pointer">
                      {noticiasFiltroForm.data.activo ?? true ? 'Activo' : 'Inactivo'}
                    </label>
                  </div>
                </>
              )}
            </div>
            <div className="flex items-center gap-2 justify-end mt-6">
              <button
                onClick={() => setNoticiasFiltroForm(null)}
                className="px-4 py-2 border border-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveNoticiasFiltro}
                disabled={saving}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white text-sm rounded-lg hover:bg-primary-dark transition-colors cursor-pointer disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <i className="ri-save-line" />
                    Guardar
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
                <i className="ri-delete-bin-line text-red-500 text-lg" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">¿Eliminar registro?</h3>
                <p className="text-sm text-gray-500">Esta acción no se puede deshacer.</p>
              </div>
            </div>
            <div className="flex items-center gap-2 justify-end">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 border border-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
              >
                Cancelar
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-500 text-white text-sm rounded-lg hover:bg-red-600 transition-colors cursor-pointer"
              >
                <i className="ri-delete-bin-line mr-1.5" />
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminPanel() {
  return (
    <ToastProvider>
      <AdminPanelInner />
    </ToastProvider>
  );
}